// src/features/records/utils/profileCache.js

/**
 * Creates a reusable cache with TTL support and size management
 * @param {Object} options - Cache configuration options
 * @param {number} options.maxSize - Maximum number of items in cache
 * @param {number} options.ttl - Time to live in milliseconds
 * @returns {Object} Cache interface
 */
export const createProfileCache = ({ maxSize = 100, ttl = 5 * 60 * 1000 } = {}) => {
  const cache = new Map();

  /**
   * Get an item from cache with TTL check
   * @param {string} key - Cache key
   * @returns {*} Cached value or undefined
   */
  const get = (key) => {
    const item = cache.get(key);
    if (!item) return undefined;

    const now = Date.now();
    if (now - item.timestamp > ttl) {
      cache.delete(key);
      return undefined;
    }

    return item.data;
  };

  /**
   * Trim cache when approaching capacity limit
   * Removes oldest 10% of entries when at 90% capacity
   */
  const trimCache = () => {
    if (cache.size > maxSize * 0.9) {
      const batchSize = Math.floor(maxSize * 0.1);
      const requiredRemoval = cache.size - maxSize + 1;
      const entriesToRemove = Math.max(1, batchSize, requiredRemoval);
      const sortedKeys = Array.from(cache.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp)
        .slice(0, entriesToRemove)
        .map(([key]) => key);
      
      sortedKeys.forEach(key => cache.delete(key));
    }
  };

  /**
   * Set an item in cache with timestamp
   * @param {string} key - Cache key
   * @param {*} value - Value to cache
   */
  const set = (key, value) => {
    // Trim cache before adding new item if needed
    trimCache();
    
    cache.set(key, {
      data: value,
      timestamp: Date.now()
    });
  };

  /**
   * Check if cache has a key with TTL validation
   * @param {string} key - Cache key
   * @returns {boolean} True if key exists and is not expired
   */
  const has = (key) => {
    const item = cache.get(key);
    if (!item) return false;

    const now = Date.now();
    if (now - item.timestamp > ttl) {
      cache.delete(key);
      return false;
    }

    return true;
  };

  /**
   * Clear all items from cache
   */
  const clear = () => {
    cache.clear();
  };

  /**
   * Remove expired entries from cache
   * @returns {number} Number of entries removed
   */
  const evictExpired = () => {
    const now = Date.now();
    let removed = 0;

    for (const [key, value] of cache.entries()) {
      if (now - value.timestamp > ttl) {
        cache.delete(key);
        removed++;
      }
    }

    return removed;
  };

  /**
   * Remove oldest entries when cache exceeds size limit
   * @param {number} percentage - Percentage of entries to remove (default 0.2 = 20%)
   * @returns {number} Number of entries removed
   */
  const evictOldest = (percentage = 0.2) => {
    if (cache.size <= maxSize) return 0;

    const sortedEntries = [...cache.entries()]
      .sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    const toRemove = Math.floor(maxSize * percentage);
    let removed = 0;

    for (let i = 0; i < toRemove && i < sortedEntries.length; i++) {
      cache.delete(sortedEntries[i][0]);
      removed++;
    }

    return removed;
  };

  /**
   * Get raw cache entry with metadata
   * @param {string} key - Cache key
   * @returns {Object|undefined} Cache entry with data and timestamp
   */
  const getRaw = (key) => {
    return cache.get(key);
  };

  /**
   * Get current cache size
   * @returns {number} Number of items in cache
   */
  const size = () => cache.size;

  return {
    get,
    set,
    has,
    clear,
    evictExpired,
    evictOldest,
    getRaw,
    size,
    maxSize,
    ttl
  };
};

export default createProfileCache;
